<script setup>
import { useCurrency } from 'dashboard/composables/useCurrency';
import { useI18n } from 'vue-i18n';

defineProps({
  report: { type: Object, default: () => ({}) },
});

const { formatCurrency } = useCurrency();
const { t } = useI18n();

const formatPercentage = value => `${value}%`;
const formatGoal = row =>
  `${row.target_appointments} (${formatPercentage(row.target_progress)})`;
const formatDate = value => (value ? new Date(value).toLocaleString() : '—');
const statusKeys = {
  scheduled: 'APPOINTMENTS.STATUS_SCHEDULED',
  confirmed: 'APPOINTMENTS.STATUS_CONFIRMED',
  completed: 'APPOINTMENTS.STATUS_COMPLETED',
  cancelled: 'APPOINTMENTS.STATUS_CANCELLED',
  no_show: 'APPOINTMENTS.STATUS_NO_SHOW',
};
const formatStatus = status => t(statusKeys[status] || status);
const displayName = name =>
  name || t('KANBAN_REPORTS.SALES_ATTRIBUTION.UNATTRIBUTED');
</script>

<template>
  <section class="space-y-4">
    <div>
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-10">
        {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <div
        v-for="metric in [
          ['APPOINTMENTS', report.summary?.appointments || 0],
          ['SDR_WINS', report.summary?.sdr_wins || 0],
          [
            'ORIGINATED_REVENUE',
            formatCurrency(report.summary?.originated_revenue || 0),
          ],
          ['DEALS_WON', report.summary?.deals_won || 0],
          ['WON_VALUE', formatCurrency(report.summary?.won_value || 0)],
        ]"
        :key="metric[0]"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <p class="text-xs text-n-slate-10">
          {{ $t(`KANBAN_REPORTS.SALES_ATTRIBUTION.${metric[0]}`) }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ metric[1] }}
        </p>
      </div>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2">
      <div class="border-b border-n-weak px-4 py-3 font-medium text-n-slate-12">
        {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.SDR_TABLE') }}
      </div>
      <table class="w-full text-sm">
        <thead class="bg-n-solid-3 text-left text-n-slate-10">
          <tr>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.SDR') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.APPOINTMENTS') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.GOAL') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.SDR_WINS') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.CONVERSION') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.ORIGINATED_REVENUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in report.sdrs"
            :key="row.id || row.name"
            class="border-t border-n-weak text-n-slate-11"
          >
            <td class="p-3 font-medium text-n-slate-12">
              {{ displayName(row.name) }}
            </td>
            <td class="p-3">{{ row.scheduled }}</td>
            <td class="p-3">
              {{ formatGoal(row) }}
            </td>
            <td class="p-3">{{ row.sdr_wins }}</td>
            <td class="p-3">{{ formatPercentage(row.conversion_rate) }}</td>
            <td class="p-3">{{ formatCurrency(row.originated_revenue) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2">
      <div class="border-b border-n-weak px-4 py-3 font-medium text-n-slate-12">
        {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.APPOINTMENT_TABLE') }}
      </div>
      <table class="w-full text-sm">
        <thead class="bg-n-solid-3 text-left text-n-slate-10">
          <tr>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.DATE') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.CONTACT') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.SDR') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.ATTENDEE') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.STATUS') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.SDR_WIN') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.VALUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in report.appointments"
            :key="row.id"
            class="border-t border-n-weak text-n-slate-11"
          >
            <td class="whitespace-nowrap p-3">
              {{ formatDate(row.scheduled_at) }}
            </td>
            <td class="p-3 text-n-slate-12">{{ row.contact || row.title }}</td>
            <td class="p-3">{{ displayName(row.sdr) }}</td>
            <td class="p-3">{{ row.attendee || '—' }}</td>
            <td class="p-3">{{ formatStatus(row.status) }}</td>
            <td class="p-3">
              {{
                row.sdr_win
                  ? $t('KANBAN_REPORTS.SALES_ATTRIBUTION.YES')
                  : $t('KANBAN_REPORTS.SALES_ATTRIBUTION.NO')
              }}
            </td>
            <td class="p-3">{{ formatCurrency(row.originated_revenue) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2">
      <div class="border-b border-n-weak px-4 py-3 font-medium text-n-slate-12">
        {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.CLOSER_TABLE') }}
      </div>
      <table class="w-full text-sm">
        <thead class="bg-n-solid-3 text-left text-n-slate-10">
          <tr>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.CLOSER') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.DEALS_WON') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.WON_VALUE') }}
            </th>
            <th class="p-3">
              {{ $t('KANBAN_REPORTS.SALES_ATTRIBUTION.AVERAGE_TICKET') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in report.business_wins"
            :key="row.id || row.name"
            class="border-t border-n-weak text-n-slate-11"
          >
            <td class="p-3 font-medium text-n-slate-12">
              {{ displayName(row.name) }}
            </td>
            <td class="p-3">{{ row.deals_won }}</td>
            <td class="p-3">{{ formatCurrency(row.won_value) }}</td>
            <td class="p-3">{{ formatCurrency(row.average_ticket) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
